/** Join optional Tailwind strings for layout JSON slots — no tailwind-merge deps in primitives. */
export function joinClasses(
  ...parts: (string | undefined | null | false)[]
): string {
  return parts.filter(Boolean).join(' ');
}

/** Prompt above field controls — readable foreground without heavy weight. */
export const BESPOKE_FIELD_LABEL_CLASS =
  'block min-w-0 text-sm font-normal leading-snug text-foreground';

/** Checkbox / radio option row text (checklists, yes/no label-aside-checkbox). */
export const BESPOKE_INLINE_CONTROL_LABEL_CLASS =
  'min-w-0 text-sm font-normal leading-snug text-muted-foreground';

/** Option label beside a checkbox or radio — matches inline control labels. */
export const BESPOKE_OPTION_LABEL_CLASS = joinClasses(
  BESPOKE_INLINE_CONTROL_LABEL_CLASS,
  'peer-disabled:cursor-not-allowed peer-disabled:opacity-50'
);

/** Disabled styling appended to clickable option labels. */
export const BESPOKE_OPTION_LABEL_DISABLED_CLASS =
  'cursor-not-allowed opacity-50';

/** Horizontal gap between a control and its option label. */
export const BESPOKE_CONTROL_LABEL_GAP_CLASS = 'gap-x-2';

/** Row wrapper for checkbox / radio + label. */
export const BESPOKE_CONTROL_ROW_CLASS = joinClasses(
  'flex min-w-0 max-w-full items-center',
  BESPOKE_CONTROL_LABEL_GAP_CLASS
);

/** Vertical gap between label and control — compact default */
export const BESPOKE_FIELD_STACK_GAP_CLASS = 'space-y-1.5';

/** Slightly looser stack for checklist / compound multiselect (many rows). */
export const BESPOKE_MULTI_STACK_GAP_CLASS = 'space-y-2';

/** Default compact single-line input sizing. DatePicker uses `datePickerSize="default"` (same h-8/text-sm). */
export const BESPOKE_INPUT_COMPACT_CLASS = 'h-8 text-sm';

/** Default compact multiline textarea */
export const BESPOKE_TEXTAREA_COMPACT_CLASS = 'min-h-16 resize-y py-2 text-sm';

/**
 * Unchecked choice-control border/fill — matches Input / SelectTrigger (`border-input`).
 * Checked states keep primary accent via DS + `BESPOKE_*_CHECKED_CLASS` below.
 */
export const BESPOKE_CHOICE_UNCHECKED_CLASS = 'border-input bg-background';

export const BESPOKE_CHECKBOX_CHECKED_CLASS =
  'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground';

export const BESPOKE_RADIO_CHECKED_CLASS =
  'data-[state=checked]:border-primary data-[state=checked]:text-primary';

/** Classes merged onto bespoke `Checkbox` instances. */
export const BESPOKE_CHECKBOX_CLASS = joinClasses(
  BESPOKE_CHOICE_UNCHECKED_CLASS,
  BESPOKE_CHECKBOX_CHECKED_CLASS
);

/** Classes merged onto bespoke `RadioGroupItem` instances. */
export const BESPOKE_RADIO_ITEM_CLASS = joinClasses(
  BESPOKE_CHOICE_UNCHECKED_CLASS,
  'text-foreground',
  BESPOKE_RADIO_CHECKED_CLASS
);

/** Schema-missing / empty-option helper copy */
export const BESPOKE_EMPTY_STATE_CLASS =
  'text-xs leading-snug text-muted-foreground';

export const BESPOKE_EMPTY_STATE_CODE_CLASS =
  'rounded bg-muted px-1 py-0.5 font-mono text-[11px]';

/** Layout `section-label` / `static` section variant — gray CMS block labels */
export const BESPOKE_SECTION_LABEL_CLASS =
  'text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2';

/** Layout `section-label` / `static` title variant — gray card headings */
export const BESPOKE_TITLE_LABEL_CLASS =
  'text-sm font-semibold leading-snug text-muted-foreground';

/** Block-level title (section-label nodes) */
export const BESPOKE_TITLE_LABEL_BLOCK_CLASS = joinClasses(
  BESPOKE_TITLE_LABEL_CLASS,
  'mb-2'
);

/** Full cell border — matches card / default app borders */
export const BESPOKE_TABLE_CELL_BORDER_CLASS = 'border border-border';

/**
 * Legacy table shell — framed card look. Used when `chrome: "legacy"` (default).
 * Scrolls horizontally on narrow viewports while clipping cell corners.
 */
export const BESPOKE_TABLE_SHELL_CLASS =
  'overflow-x-auto overflow-y-hidden rounded-lg border border-border bg-card';

/**
 * Structural shell for `chrome: "none"` — overflow only; author `className` /
 * `style` own border/background (same idea as `stack` / `box`).
 */
export const BESPOKE_TABLE_SHELL_STRUCTURAL_CLASS =
  'overflow-x-auto overflow-y-hidden min-w-0';

/** Perimeter cell borders omitted — shell draws the outer frame when framed */
export const BESPOKE_TABLE_ELEMENT_CLASS = joinClasses(
  'w-full border-collapse',
  '[&_thead_tr:first-child_th]:border-t-0',
  '[&_tbody_tr:last-child_td]:border-b-0',
  '[&_tr>_:first-child]:border-l-0',
  '[&_tr>_:last-child]:border-r-0'
);

/** Density padding / type scale (`variant`) — no colors */
export const BESPOKE_TABLE_DENSITY_COMPACT_PAD_CLASS = 'px-2 py-1.5 text-xs';
export const BESPOKE_TABLE_DENSITY_DEFAULT_PAD_CLASS = 'px-3 py-2.5 text-sm';
export const BESPOKE_TABLE_DENSITY_LOOSE_PAD_CLASS = 'px-3 py-3 text-sm';

/** Structural cell slots for `chrome: "none"` (border + density + alignment) */
export const BESPOKE_TABLE_STRUCTURAL_ROW_HEADER_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'text-left'
);
export const BESPOKE_TABLE_STRUCTURAL_COL_HEADER_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'text-center'
);
export const BESPOKE_TABLE_STRUCTURAL_ROW_LABEL_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'text-left'
);
export const BESPOKE_TABLE_STRUCTURAL_CELL_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'text-center align-middle'
);

/** Legacy chrome — header text on light primary wash (`chrome: "legacy"`) */
export const BESPOKE_TABLE_ROW_HEADER_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'bg-primary/5 px-3 py-2.5 text-left text-sm font-medium text-foreground'
);

export const BESPOKE_TABLE_COL_HEADER_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'bg-primary/5 px-3 py-2.5 text-center text-sm font-medium text-foreground'
);

export const BESPOKE_TABLE_ROW_LABEL_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'bg-muted/20 px-3 py-2.5 text-sm font-medium text-foreground'
);

export const BESPOKE_TABLE_CELL_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'px-3 py-2.5 text-center align-middle'
);

/** Legacy compact matrix — tighter padding, same primary-wash language */
export const BESPOKE_TABLE_COMPACT_ROW_HEADER_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'bg-primary/5 px-2 py-1.5 text-left text-xs font-medium text-foreground'
);

export const BESPOKE_TABLE_COMPACT_COL_HEADER_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'bg-primary/5 px-2 py-1.5 text-center text-xs font-medium text-foreground'
);

export const BESPOKE_TABLE_COMPACT_ROW_LABEL_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'bg-muted/20 px-2 py-1.5 text-xs font-medium text-foreground'
);

export const BESPOKE_TABLE_COMPACT_CELL_CLASS = joinClasses(
  BESPOKE_TABLE_CELL_BORDER_CLASS,
  'px-2 py-1.5 text-center align-middle text-xs'
);

/** Legacy body row hover / zebra (`chrome: "legacy"` only) */
export const BESPOKE_TABLE_BODY_ROW_CLASS =
  'transition-colors even:bg-muted/10 hover:bg-muted/25';
